import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import Toast from '../../../components/ui/Toast';
import { useToast } from '../../../hooks/useToast';
import { SRI_LANKA_DISTRICTS, SRI_LANKA_CITIES } from '../../../utils/locations';

export default function BarberSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    bio: '',
    district: '',
    service_cities: [],
    push_notifications: true,
    email_notifications: true
  });

  const [districtSearch, setDistrictSearch] = useState('');
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { toast, showToast, hideToast } = useToast();

  const filteredDistricts = SRI_LANKA_DISTRICTS.filter(d => 
    d.toLowerCase().includes(districtSearch.toLowerCase())
  );

  const availableCities = profile.district ? (SRI_LANKA_CITIES[profile.district] || []).filter(
    c => c.toLowerCase().includes(citySearch.toLowerCase()) && !profile.service_cities.includes(c)
  ) : [];

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: dbProfile } = await supabase
          .from('profiles_1740423033529')
          .select('*')
          .eq('id', user.id)
          .single();

        setProfile({
          full_name: dbProfile?.full_name || '',
          email: user.email,
          bio: dbProfile?.bio || '',
          district: dbProfile?.district || '',
          service_cities: dbProfile?.service_cities || [],
          push_notifications: true,
          email_notifications: true
        });
        setDistrictSearch(dbProfile?.district || '');
      }
      setLoading(false);
    };
    fetchProfile();
  }, []);

  const handleSelectDistrict = (d) => {
    setProfile(prev => ({ ...prev, district: d, service_cities: [] }));
    setDistrictSearch(d);
    setShowDistrictDropdown(false);
    setCitySearch('');
  };

  const handleAddCity = (city) => {
    setProfile(prev => ({ ...prev, service_cities: [...prev.service_cities, city] }));
    setCitySearch('');
    setShowCityDropdown(false);
  };

  const handleRemoveCity = (city) => {
    setProfile(prev => ({ ...prev, service_cities: prev.service_cities.filter(c => c !== city) }));
  };

  const handleSave = async () => {
    if (!profile.district) {
      showToast('Please select a district.', 'error');
      return;
    }
    if (profile.service_cities.length === 0) {
      showToast('Please select at least one service city.', 'error');
      return;
    }
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('profiles_1740423033529')
        .update({
          full_name: profile.full_name,
          bio: profile.bio,
          district: profile.district,
          service_cities: profile.service_cities,
          service_districts: profile.district ? [profile.district] : [],
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;
      showToast('Profile updated successfully!', 'success');
    } catch (err) {
      showToast('Error updating profile. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'CONFIRM') return;
    setDeleting(true);
    try {
      const { error } = await supabase.rpc('delete_own_account');
      if (error) throw error;
      await supabase.auth.signOut();
      // Redirect to login page instead of landing
      navigate('/login', { replace: true });
    } catch (err) {
      showToast('Failed to delete account: ' + err.message, 'error');
      setDeleting(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading settings...</div>;

  return (
    <div className="animate-in fade-in duration-500 max-w-4xl pb-20">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      
      <h1 className="text-3xl font-black text-brand-dark mb-8 tracking-tight">Settings</h1>

      <div className="space-y-6">
        {/* Profile Section */}
        <section className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center">
              <SafeIcon name="User" className="text-brand-primary text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-black text-brand-dark">Profile Information</h2>
              <p className="text-sm text-gray-500">Update your name, bio, and service area.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Display Name</label>
              <input 
                type="text" 
                value={profile.full_name} 
                onChange={(e) => setProfile({...profile, full_name: e.target.value})}
                className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-brand-primary font-bold"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Email Address</label>
              <input 
                type="email" 
                disabled 
                value={profile.email} 
                className="w-full p-4 bg-gray-100 border border-gray-100 rounded-2xl text-gray-500 cursor-not-allowed font-bold"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Professional Bio</label>
              <textarea 
                rows="3" 
                value={profile.bio} 
                onChange={(e) => setProfile({...profile, bio: e.target.value})}
                className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-brand-primary font-medium" 
                placeholder="Tell clients about your expertise..."
              />
            </div>
          </div>
        </section>

        {/* Service Area Section */}
        <section className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center">
              <SafeIcon name="MapPin" className="text-brand-primary text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-black text-brand-dark">Service Area</h2>
              <p className="text-sm text-gray-500">Update your operating district and service cities.</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* District Selector */}
            <div className="relative">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Your District *</label>
              <div className="relative">
                <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                <input
                  type="text"
                  placeholder="Search district..."
                  value={districtSearch}
                  onChange={(e) => {
                    setDistrictSearch(e.target.value);
                    setProfile(prev => ({ ...prev, district: '', service_cities: [] }));
                    setShowDistrictDropdown(true);
                  }}
                  onFocus={() => setShowDistrictDropdown(true)}
                  className="w-full pl-12 pr-10 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-bold text-brand-dark"
                />
                {profile.district && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <SafeIcon name="CheckCircle" className="text-green-500" />
                  </div>
                )}
              </div>
              
              {showDistrictDropdown && !profile.district && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowDistrictDropdown(false)}></div>
                  <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-56 overflow-y-auto py-2">
                    {filteredDistricts.length > 0 ? filteredDistricts.map(d => (
                      <div 
                        key={d} 
                        className="px-5 py-3 hover:bg-brand-primary/5 cursor-pointer font-bold text-brand-dark text-sm transition-colors"
                        onClick={() => handleSelectDistrict(d)}
                      >
                        {d}
                      </div>
                    )) : <div className="px-5 py-3 text-gray-400 text-sm italic">No districts found</div>}
                  </div>
                </>
              )}

              {profile.district && (
                <p className="text-[10px] text-brand-primary font-black uppercase tracking-widest mt-1 pl-1">
                  Selected: {profile.district}
                </p>
              )}
            </div>

            {/* Service Cities */}
            {profile.district && (
              <div className="relative animate-in fade-in slide-in-from-top-2">
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                  Service Cities in {profile.district} *
                </label>
                <div 
                  className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 flex flex-wrap gap-2 cursor-pointer focus-within:border-brand-primary transition-colors min-h-[56px]"
                  onClick={() => setShowCityDropdown(true)}
                >
                  {profile.service_cities.map(c => (
                    <span key={c} className="bg-brand-primary text-white text-xs px-3 py-1 rounded-lg font-bold flex items-center gap-1">
                      {c}
                      <button className="ml-1 hover:text-red-200" onClick={(e) => { e.stopPropagation(); handleRemoveCity(c); }}>
                        <SafeIcon name="X" className="text-[10px]" />
                      </button>
                    </span>
                  ))}
                  <input 
                    type="text" 
                    placeholder={profile.service_cities.length === 0 ? "Search & select cities you service" : ""}
                    value={citySearch}
                    onChange={e => { setCitySearch(e.target.value); setShowCityDropdown(true); }}
                    className="bg-transparent outline-none flex-1 min-w-[150px] font-bold text-brand-dark"
                  />
                </div>

                {showCityDropdown && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowCityDropdown(false)}></div>
                    <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-56 overflow-y-auto py-2">
                      {availableCities.length > 0 ? availableCities.map(c => (
                        <div 
                          key={c} 
                          className="px-5 py-3 hover:bg-brand-primary/5 cursor-pointer font-bold text-brand-dark text-sm transition-colors"
                          onClick={() => handleAddCity(c)}
                        >
                          {c}
                        </div>
                      )) : (
                        <div className="px-5 py-3 text-gray-400 text-sm italic">
                          {(SRI_LANKA_CITIES[profile.district] || []).length === profile.service_cities.length ? 'All cities selected' : 'No cities found'}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {profile.service_cities.length > 0 && (
                  <p className="text-[10px] text-brand-primary font-black uppercase tracking-widest mt-1 pl-1">
                    {profile.service_cities.length} {profile.service_cities.length === 1 ? 'city' : 'cities'} selected
                  </p>
                )}
              </div>
            )}

            {/* Info Banner */}
            <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex gap-3">
              <SafeIcon name="Info" className="text-brand-primary mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700 leading-relaxed">
                <strong>Moving to a new area?</strong> Updating your district and service cities will immediately change which customers can discover and book you. Existing bookings are not affected.
              </p>
            </div>
          </div>
        </section>

        {/* Notifications Section */}
        <section className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center">
              <SafeIcon name="Bell" className="text-brand-primary text-2xl" />
            </div>
            <h2 className="text-xl font-black text-brand-dark">Notifications</h2>
          </div>

          <div className="space-y-4">
            {[
              { key: 'push_notifications', label: 'Push Notifications', desc: 'Receive alerts for new bookings on your phone.' },
              { key: 'email_notifications', label: 'Email Notifications', desc: 'Get booking confirmations and daily summaries.' }
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between py-2">
                <div>
                  <p className="font-bold text-brand-dark">{item.label}</p>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
                <button 
                  onClick={() => setProfile({...profile, [item.key]: !profile[item.key]})}
                  className={`w-12 h-6 rounded-full relative transition-colors ${profile[item.key] ? 'bg-brand-primary' : 'bg-gray-200'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${profile[item.key] ? 'left-7' : 'left-1'}`} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="px-10 py-4 font-black text-base shadow-xl">
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>

        {/* Danger Zone */}
        <section className="bg-red-50 p-8 rounded-[2rem] border border-red-100 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <SafeIcon name="AlertTriangle" className="text-red-600 text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-black text-red-900">Danger Zone</h2>
              <p className="text-sm text-red-700">Permanently delete your barber account and all associated data.</p>
            </div>
          </div>
          <Button onClick={() => setShowDeleteModal(true)} className="bg-red-600 hover:bg-red-700 text-white border-none">
            <SafeIcon name="Trash2" className="mr-2" />
            Delete My Account
          </Button>
        </section>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-brand-dark/80 z-[100] flex items-center justify-center p-6 backdrop-blur-md animate-in fade-in">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full text-center shadow-2xl animate-in zoom-in-95">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="AlertTriangle" className="text-3xl" />
            </div>
            <h2 className="text-xl font-black text-brand-dark mb-2">Delete Account?</h2>
            <p className="text-gray-500 font-medium text-sm mb-6 leading-relaxed">
              This will <strong className="text-red-600">permanently delete</strong> your barber account. Type <strong className="text-red-600">CONFIRM</strong> below.
            </p>
            <input 
              type="text" placeholder="Type CONFIRM" 
              value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl mb-6 text-center font-black outline-none focus:border-red-500"
            />
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white border-none" disabled={deleteConfirmText !== 'CONFIRM' || deleting} onClick={handleDeleteAccount}>
                {deleting ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}